import { useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import { Modal, Field, Badge, Empty, STATUS_TONE, fmtDate, label } from './ui.jsx';
import { useConfirm } from './feedback.jsx';
import { t } from '../i18n/index.jsx';
import FileViewer from './FileViewer.jsx';
import { IconPlus } from './icons.jsx';

const API_BASE = import.meta.env.VITE_API_BASE || '/api';
const DOC_STATUSES = ['to_fill', 'filled', 'submitted', 'approved', 'rejected'];
const kb = (b) => `${(Number(b || 0) / 1024).toFixed(0)} KB`;

/**
 * Reusable document panel for any owner (fund template library, fund_client
 * instances, sal attachments…). Handles upload, per-version notes, version
 * history and status changes. `reloadSignal` (a number) forces a refetch when
 * the parent duplicates a template into this owner.
 */
export default function DocumentList({
  ownerType, ownerId, kind = 'attachment', title = 'Documents',
  canEdit = false, isAdmin = false, reloadSignal = 0,
}) {
  const confirm = useConfirm();
  const [docs, setDocs] = useState(null);
  const [adding, setAdding] = useState(false);
  const [historyDoc, setHistoryDoc] = useState(null);
  const [viewFile, setViewFile] = useState(null);
  const [trashed, setTrashed] = useState(false);

  async function load() {
    const r = await api.get(`/documents?owner_type=${ownerType}&owner_id=${ownerId}${trashed ? '&trashed=1' : ''}`);
    setDocs(r.data);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ownerType, ownerId, reloadSignal, trashed]);

  async function setStatus(doc, status) {
    await api.patch(`/documents/${doc.id}`, { status });
    load();
  }
  async function removeDoc(doc) {
    if (!(await confirm({ title: t('Move to trash'), message: t('Move "{title}" to trash? Versions are kept and it can be restored.', { title: doc.title }), confirmLabel: t('Move to trash') }))) return;
    await api.del(`/documents/${doc.id}`);
    load();
  }
  async function restoreDoc(doc) {
    await api.post(`/documents/${doc.id}/restore`);
    load();
  }
  async function purgeDoc(doc) {
    if (!(await confirm({ title: t('Delete permanently'), message: t('Permanently delete "{title}" and its files? This cannot be undone.', { title: doc.title }), confirmLabel: t('Delete permanently'), danger: true }))) return;
    await api.del(`/documents/${doc.id}/purge`);
    load();
  }

  return (
    <section className="panel">
      <div className="panel-head">
        <h2 className="panel-title">{title}{trashed ? ` — ${t('Trash')}` : ''}</h2>
        <div className="head-actions">
          {canEdit && (
            <button className="btn ghost sm" onClick={() => setTrashed((v) => !v)}>
              {trashed ? t('Back to documents') : t('Trash')}
            </button>
          )}
          {canEdit && !trashed && (
            <button className="btn ghost sm" onClick={() => setAdding(true)}>
              <IconPlus width={13} height={13} /> {kind === 'template' ? t('Add template') : t('Add document')}
            </button>
          )}
        </div>
      </div>

      {!docs ? null : docs.length === 0 ? <Empty>{trashed ? t('Trash is empty.') : t('No documents.')}</Empty> : (
        <ul className="line-list">
          {docs.map((d) => (
            <li key={d.id}>
              <div className="ll-main">
                <span className="ll-title">{d.title}</span>
                <span className="muted sm">
                  {d.doc_type ? `${d.doc_type} · ` : ''}
                  {d.original_name ? `${d.original_name} · ${kb(d.size_bytes)}` : t('no file')}
                  {d.current_version > 1 ? ` · v${d.current_version}` : ''}
                </span>
              </div>
              <div className="ll-meta">
                {trashed ? (
                  <>
                    {canEdit && <button className="btn ghost sm" onClick={() => restoreDoc(d)}>{t('Restore')}</button>}
                    {isAdmin && <button className="btn danger ghost sm" onClick={() => purgeDoc(d)}>{t('Delete permanently')}</button>}
                  </>
                ) : (
                  <>
                    {d.status && <Badge tone={STATUS_TONE[d.status] || 'gray'}>{label(d.status)}</Badge>}
                    {d.original_name && (
                      <button className="btn ghost sm" onClick={() => setViewFile({ name: d.original_name, docId: d.id })}>{t('View')}</button>
                    )}
                    <button className="btn ghost sm" onClick={() => setHistoryDoc(d)}>
                      {canEdit ? t('Versions') : t('History')}
                    </button>
                    {canEdit && (
                      <select className="inline-select sm" value={d.status || ''}
                        onChange={(e) => setStatus(d, e.target.value)} title={t('Status')}>
                        <option value="">—</option>
                        {DOC_STATUSES.map((s) => <option key={s} value={s}>{label(s)}</option>)}
                      </select>
                    )}
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {adding && (
        <AddDocumentModal ownerType={ownerType} ownerId={ownerId} kind={kind}
          onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />
      )}
      {historyDoc && (
        <VersionsModal doc={historyDoc} canEdit={canEdit}
          onClose={() => setHistoryDoc(null)}
          onChanged={() => load()}
          onView={(v) => setViewFile({ path: `/document-versions/${v.id}/download`, name: v.original_name })}
          onDelete={canEdit ? () => { removeDoc(historyDoc); setHistoryDoc(null); } : null} />
      )}
      {viewFile && <ViewFileResolver viewFile={viewFile} onClose={() => setViewFile(null)} />}
    </section>
  );
}

/** Resolves the "latest version" download path before opening the viewer. */
function ViewFileResolver({ viewFile, onClose }) {
  const [resolved, setResolved] = useState(viewFile.docId ? null : viewFile);
  useEffect(() => {
    if (!viewFile.docId) return;
    api.get(`/documents/${viewFile.docId}`).then((r) => {
      const latest = (r.data.versions || [])[0];
      if (latest) setResolved({ path: `/document-versions/${latest.id}/download`, name: latest.original_name });
      else onClose();
    }).catch(onClose);
  }, [viewFile, onClose]);
  if (!resolved) return null;
  return <FileViewer path={resolved.path} name={resolved.name} onClose={onClose} />;
}

function AddDocumentModal({ ownerType, ownerId, kind, onClose, onSaved }) {
  const fileRef = useRef(null);
  const [title, setTitle] = useState('');
  const [docType, setDocType] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  async function save() {
    const file = fileRef.current?.files?.[0];
    if (!file) { setErr(t('Choose a file to upload.')); return; }
    setBusy(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('owner_type', ownerType);
      fd.append('owner_id', ownerId);
      fd.append('kind', kind);
      fd.append('title', title);
      fd.append('doc_type', docType);
      fd.append('note', note);
      fd.append('file', file);
      await api.upload('/documents', fd);
      onSaved();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  return (
    <Modal title={kind === 'template' ? t('Add master template') : t('Add document')} onClose={onClose}
      footer={
        <div className="modal-actions">
          <div className="spacer" />
          <button className="btn ghost" onClick={onClose}>{t('Cancel')}</button>
          <button className="btn primary" onClick={save} disabled={busy}>{busy ? t('Uploading…') : t('Upload')}</button>
        </div>
      }>
      {err && <div className="alert error">{err}</div>}
      <Field label={t('Title (optional — defaults to filename)')}><input value={title} onChange={(e) => setTitle(e.target.value)} /></Field>
      <Field label={t('Type / label (optional)')}><input value={docType} onChange={(e) => setDocType(e.target.value)} placeholder="modulo, allegato, visura…" /></Field>
      <Field label={t('File')}><input ref={fileRef} type="file" /></Field>
      <Field label={t('Note (optional)')}><textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} /></Field>
    </Modal>
  );
}

function VersionsModal({ doc, canEdit, onClose, onChanged, onView, onDelete }) {
  const confirm = useConfirm();
  const [full, setFull] = useState(null);
  const fileRef = useRef(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  async function load() {
    const r = await api.get(`/documents/${doc.id}`);
    setFull(r.data);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [doc.id]);

  async function addVersion() {
    const file = fileRef.current?.files?.[0];
    if (!file) { setErr(t('Choose the filled file to upload.')); return; }
    setBusy(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('note', note);
      fd.append('file', file);
      await api.upload(`/documents/${doc.id}/versions`, fd);
      setNote(''); if (fileRef.current) fileRef.current.value = '';
      await load();
      onChanged();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  async function restoreVersion(v) {
    if (!(await confirm({ title: t('Restore version'), message: t('Restore v{n} as the current version? (history is preserved)', { n: v.version }), confirmLabel: t('Restore') }))) return;
    setErr('');
    try {
      await api.post(`/documents/${doc.id}/restore-version/${v.id}`);
      await load();
      onChanged();
    } catch (e) { setErr(e.message); }
  }

  const latestVersion = full && full.versions && full.versions.length ? full.versions[0].version : null;

  return (
    <Modal title={`${t('Versions')} — ${doc.title}`} onClose={onClose}
      footer={
        <div className="modal-actions">
          {onDelete && <button className="btn danger ghost" onClick={onDelete}>{t('Delete document')}</button>}
          <div className="spacer" />
          <button className="btn ghost" onClick={onClose}>{t('Close')}</button>
        </div>
      }>
      {err && <div className="alert error">{err}</div>}
      {!full ? null : (full.versions || []).length === 0 ? <Empty>{t('No versions yet.')}</Empty> : (
        <ul className="line-list">
          {full.versions.map((v) => (
            <li key={v.id}>
              <div className="ll-main">
                <span className="ll-title">v{v.version} · {v.original_name}</span>
                <span className="muted sm">
                  {kb(v.size_bytes)} · {fmtDate(v.created_at?.slice(0, 10))}
                  {v.uploaded_by_name ? ` · ${v.uploaded_by_name}` : ''}
                  {v.note ? ` — ${v.note}` : ''}
                </span>
              </div>
              <div className="ll-meta">
                <button className="btn ghost sm" onClick={() => onView(v)}>{t('View')}</button>
                <a className="btn ghost sm" href={`${API_BASE}/document-versions/${v.id}/download`}>{t('Download')}</a>
                {canEdit && v.version !== latestVersion && (
                  <button className="btn ghost sm" onClick={() => restoreVersion(v)}>{t('Restore')}</button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {canEdit && (
        <div className="add-version">
          <h4 className="panel-title" style={{ marginTop: 16 }}>{t('Upload new version')}</h4>
          <Field label={t('Filled file')}><input ref={fileRef} type="file" /></Field>
          <Field label={t('Note (what changed)')}><textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} /></Field>
          <button className="btn primary sm" onClick={addVersion} disabled={busy}>{busy ? t('Uploading…') : t('Add version')}</button>
        </div>
      )}
    </Modal>
  );
}
